import { useLayoutEffect, useState } from 'react'
import { getAppScrollElement } from '../utils/appScroll'
import type { ScrollportMetrics } from '../utils/deckPaneViewport'

/**
 * The app scrollport's live client height and vertical padding — what the
 * desktop deck screen's frame is sized from (`deckScreenFrame`). Measured off
 * the app's one scrolling element, never assumed.
 *
 * Same discipline as `useMeasuredHeight`: the number a layout needs is the one
 * the browser is currently rendering, so it is observed rather than written
 * down. What is observed here is the SCROLLPORT — `<main id="app-scroll">`,
 * which Layout owns (ADR-067 Decision 3). The padding is returned raw rather
 * than pre-subtracted because the frame uses the two sides differently: the
 * top padding is where the frame starts, the bottom padding is what the frame
 * reclaims (see `deckScreenFrame`). This hook used to be `useStickyPaneBox`
 * and returned the content box, which is how the bottom padding came to be
 * subtracted from the frame as a strip nobody drew.
 *
 * The element is found by the id Layout declares, not by walking up from the
 * page looking for the nearest scrollable ancestor — a search would answer
 * differently the moment a wrapper gained an `overflow`, which is the same
 * reason `deckRowRegistry` has the layout declare its scrollport rather than
 * discovering one.
 *
 * Returns `null` until the scrollport has been measured, so a caller renders
 * its own natural height for the first frame rather than a guess.
 */
export function useScrollportMetrics(): ScrollportMetrics | null {
  const [metrics, setMetrics] = useState<ScrollportMetrics | null>(null)

  // `useLayoutEffect`, and not for the usual flicker reason. The first
  // measurement necessarily changes state, and this hook's caller is a page
  // whose deep-link arrival runs in a passive effect and re-aims a motion it
  // has already started whenever the rows re-register. A measurement landing in
  // a passive effect would therefore schedule a render AFTER that arrival began
  // and re-aim it mid-flight — two writes for one motion, which ADR-067
  // Decision D forbids by construction. Measured in the layout phase, the box
  // is settled before any passive effect on the page runs at all, and no later
  // render is owed to it.
  useLayoutEffect(() => {
    const el = getAppScrollElement()
    if (!el) return

    const measure = () => {
      const cs = window.getComputedStyle(el)
      const next: ScrollportMetrics = {
        clientHeight: el.clientHeight,
        paddingTop: parseFloat(cs.paddingTop) || 0,
        paddingBottom: parseFloat(cs.paddingBottom) || 0,
      }
      // Only on a real change: this observer's own subject contains the frame
      // it sizes, so writing state on every callback would be a loop. None of
      // the three values depends on the frame's height, so an unchanged
      // measurement settles on the first pass.
      setMetrics((prev) => (prev
        && prev.clientHeight === next.clientHeight
        && prev.paddingTop === next.paddingTop
        && prev.paddingBottom === next.paddingBottom ? prev : next))
    }

    measure()

    // The padding is media-query driven, so a width change matters as much as
    // a height change.
    window.addEventListener('resize', measure)
    // jsdom and SSR have no ResizeObserver; the synchronous measure above is
    // all those environments can offer, and constructing it unguarded throws.
    let observer: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure)
      observer.observe(el)
    }
    return () => {
      window.removeEventListener('resize', measure)
      observer?.disconnect()
    }
  }, [])

  return metrics
}
